import { extractErrorCode } from '@/lib/error/code'
import { auth } from '@/lib/firebase'
import axios, { type AxiosResponse } from 'axios'

const axiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_URL as string | undefined,
})

// Cancel abandoned GET reads on route change so rapid page-switching can't pile
// up hundreds of concurrent requests and exhaust the backend DB connection pool.
// Only GETs are tracked — in-flight writes (POST/PUT/DELETE) always complete.
const pendingGets = new Set<AbortController>()

// Reads that belong to no single route and must survive redirects — the multi-hop
// login → select-tenant → home chain would otherwise abort them and stall the app.
// Exact paths are the always-mounted AuthProvider bootstrap reads; the `/master/`
// prefix covers cross-tenant/setup endpoints (e.g. the tenant-selection list).
// None are per-tenant dashboard reads, so exempting them doesn't weaken flush
// protection. Kept out of pendingGets entirely.
const ROUTE_INDEPENDENT_GETS = [
  '/me/profile',
  '/me/profile/role',
  '/club-info',
  '/roles/categories',
]
const ROUTE_INDEPENDENT_PREFIXES = ['/master/']

function isCancellableGet(config: { method?: string; url?: string; signal?: unknown }): boolean {
  if ((config.method ?? 'get').toLowerCase() !== 'get' || config.signal) return false
  const path = (config.url ?? '').split('?')[0]
  if (ROUTE_INDEPENDENT_GETS.includes(path)) return false
  if (ROUTE_INDEPENDENT_PREFIXES.some((prefix) => path.startsWith(prefix))) return false
  return true
}

export function snapshotPendingGets(): AbortController[] {
  return [...pendingGets]
}

axiosInstance.interceptors.request.use(async (config) => {
  if (!navigator.onLine) {
    return Promise.reject(new Error('Internet connection lost.'))
  }

  if (isCancellableGet(config)) {
    const controller = new AbortController()
    config.signal = controller.signal
    ;(config as { __abortController?: AbortController }).__abortController = controller
    pendingGets.add(controller)
  }

  const tenantId = localStorage.getItem('tenantId')
  if (tenantId) {
    config.headers['x-tenant-id'] = tenantId
  }

  const user = auth.currentUser
  if (user) {
    const token = await user.getIdToken()
    config.headers.Authorization = `Bearer ${token}`

    // 'x-skip-branch-id' lets a request opt out of the branch-id header —
    // /me/profile/branches must go headerless so branch resolution can run
    // without first deleting the stored selection (which left every other
    // in-flight request headerless → 400 on @RequireBranch endpoints).
    if (config.headers['x-skip-branch-id']) {
      delete config.headers['x-skip-branch-id']
    } else if (config.headers['branch-id'] == null) {
      // A per-request 'branch-id' (branchIdOverride) wins over the sidebar's
      // stored selection — e.g. mutating a booking that belongs to another
      // branch from a cross-branch list. BE still authorizes the user against
      // that branch (@RequireBranch).
      const currentBranch = localStorage.getItem('currentBranch')
      if (
        currentBranch &&
        currentBranch !== 'undefined' &&
        currentBranch !== 'null' &&
        !isNaN(Number(currentBranch))
      ) {
        config.headers['branch-id'] = currentBranch
      }
    }

    config.headers['platform'] = 'crm'
  }
  return config
})

function releaseController(config: unknown) {
  const controller = (config as { __abortController?: AbortController } | undefined)
    ?.__abortController
  if (controller) pendingGets.delete(controller)
}

// QTY-2042: the backend rejects every authenticated request from a user whose
// account has been set In-Active (permission guard, 403 + a stable code) — it
// is not just a login-time check. Catching it here is what makes a staff
// member who is deactivated mid-session get signed out: each open tab runs its
// own queries, so the next one to fire in each tab trips this.
//
// Only the caller's own status belongs here. `MEMBER_INACTIVE` describes the
// *subject* of a request — a deactivated member someone is operating on — and
// members never authenticate against the CRM, so treating it as a revoked
// session would sign a working staff user out mid-task over somebody else's
// status.
const ACCOUNT_INACTIVE_CODES = new Set(['STAFF_INACTIVE'])

type AccountRevokedHandler = () => void
let accountRevokedHandler: AccountRevokedHandler | null = null

/**
 * Register what to do when the backend reports the signed-in account is
 * inactive. This module can't reach React context, so AuthProvider hands its
 * logout down through here. Returns an unsubscribe.
 */
export function onAccountRevoked(handler: AccountRevokedHandler): () => void {
  accountRevokedHandler = handler
  return () => {
    if (accountRevokedHandler === handler) accountRevokedHandler = null
  }
}

function isAccountInactiveError(error: any): boolean {
  if (error?.response?.status !== 403) return false
  // `extractErrorCode` also recognizes the bare UPPER_SNAKE token the backend
  // returns in `message` today — the only shape it actually sends for policy
  // verdicts, so reading `errorCode`/`code` alone would let the lockout
  // silently never fire. Imported from the leaf module to avoid a cycle:
  // `@/lib/error` imports `getStatusCode` from here.
  const code = extractErrorCode(error)
  return code != null && ACCOUNT_INACTIVE_CODES.has(code)
}

axiosInstance.interceptors.response.use(
  (response) => {
    releaseController(response.config)
    return response
  },
  (error) => {
    releaseController(error?.config)
    if (isAccountInactiveError(error)) {
      // Fire and let the error continue to propagate — the caller still sees a
      // rejected promise, the provider handles the sign-out.
      accountRevokedHandler?.()
    }
    throw error
  }
)

function getErrorMessage(error: any): string {
  const defaultMessage = 'Something went wrong.'
  if (error?.response?.errors?.length) {
    return error.response.errors[0]?.message ?? defaultMessage
  }

  return error?.response?.data?.message ?? error?.message ?? defaultMessage
}

async function getBlobErrorMessage(error: any): Promise<string> {
  const defaultMessage = 'Something went wrong.'
  if (error?.response?.data instanceof Blob) {
    try {
      const text = await (error.response.data as Blob).text()
      const json = JSON.parse(text) as { message?: string }
      return json?.message ?? defaultMessage
    } catch {
      return defaultMessage
    }
  }
  return getErrorMessage(error)
}

function getStatusCode(error: any): number | null {
  if (error?.response?.errors?.length) {
    return error.response.errors[0]?.status ?? null
  }

  return error?.response?.status ?? null
}

async function wrapApiCall<T>(fn: () => Promise<AxiosResponse<T>>): Promise<AxiosResponse<T>> {
  try {
    const data = await fn()
    return data
  } catch (error: any) {
    if (!axios.isCancel(error)) console.error('[API Error]', error)
    throw error
  }
}

export { axiosInstance as axios, getBlobErrorMessage, getErrorMessage, getStatusCode, wrapApiCall }
